import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// Handles the inline edit modal for PoTrack rows.
// On row click: loads the edit form from the server via Turbo Frame (record comes from DB).
// On frame render: shows the dialog and initializes flatpickr on date/time inputs.
// Form submits via Turbo; controller responds with turbo_stream (replace row, clear modal, toast).
export default class extends Controller {
  static targets = ["dialog", "errors", "dateInput", "timeInput"]
  static values = { frameId: { type: String, default: "po-track-edit-modal-frame" } }

  connect() {
    this.flatpickrInstances = []
    this.boundOnFrameRender = this.onFrameRender.bind(this)
    this.boundOnBeforeStreamRender = this.onBeforeStreamRender.bind(this)
    document.addEventListener("turbo:frame-render", this.boundOnFrameRender)
    document.addEventListener("turbo:before-stream-render", this.boundOnBeforeStreamRender)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-render", this.boundOnFrameRender)
    document.removeEventListener("turbo:before-stream-render", this.boundOnBeforeStreamRender)
    this.destroyFlatpickr()
  }

  onFrameRender(event) {
    if (event.target.id !== this.frameIdValue) return
    this.initFlatpickrInForm()
    if (this.hasDialogTarget) this.dialogTarget.showModal()
  }

  // A failed save replaces the open dialog via turbo stream. The new dialog
  // stays closed unless we open it after the stream renders.
  onBeforeStreamRender(event) {
    const stream = event.target
    if (!(stream instanceof Element)) return
    if (stream.getAttribute("target") !== this.frameIdValue) return

    const defaultRender = event.detail.render
    event.detail.render = (currentElement) => {
      defaultRender(currentElement)
      this.reopenDialogFromFrame()
    }
  }

  initFlatpickrInForm() {
    if (!this.hasDialogTarget) return
    this.initFlatpickrIn(this.dialogTarget)
  }

  destroyFlatpickr() {
    this.flatpickrInstances?.forEach((instance) => instance.destroy())
    this.flatpickrInstances = []
  }

  open(event) {
    if (event.target.closest('input[type="checkbox"]')) return

    const row = event.currentTarget
    const editUrl = row.dataset.editUrl

    if (!editUrl) return

    this.clearErrors()
    Turbo.visit(editUrl, { frame: this.frameIdValue })
  }

  close() {
    if (this.hasDialogTarget) this.dialogTarget.close()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  clearErrors() {
    if (this.hasErrorsTarget) {
      this.errorsTarget.textContent = ""
      this.errorsTarget.classList.add("hidden")
    }
  }

  reopenDialogFromFrame() {
    requestAnimationFrame(() => {
      const frame = document.getElementById(this.frameIdValue)
      const dialog = frame?.querySelector("dialog")
      if (!dialog) return

      this.initFlatpickrIn(dialog)
      if (!dialog.open) dialog.showModal()
    })
  }

  initFlatpickrIn(dialog) {
    this.destroyFlatpickr()
    dialog.querySelectorAll("[data-po-track-edit-target='dateInput']").forEach((input) => {
      this.flatpickrInstances.push(flatpickr(input, {
        dateFormat: "Y-m-d",
        allowInput: true,
        disableMobile: true,
        appendTo: dialog
      }))
    })
    dialog.querySelectorAll("[data-po-track-edit-target='timeInput']").forEach((input) => {
      this.flatpickrInstances.push(flatpickr(input, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "h:i K",
        time_24hr: false,
        allowInput: true,
        disableMobile: true,
        appendTo: dialog
      }))
    })
  }
}
